/**
 * SiteFooter — multi-column footer fed by GET /api/site-info.
 * Layout inspired by Le Labo: columns left, newsletter + socials right.
 * Falls back gracefully if the endpoint is unavailable.
 * Policy/info links open LegalModal inline — no page navigation required.
 */
import { useState, useEffect } from "react";
import LegalModal from "./LegalModal";

const BACKEND = process.env.REACT_APP_BACKEND_URL;
const API     = `${BACKEND}/api`;

// Maps footer link labels → modal keys in LegalModal
const LEGAL_LINK_MAP = {
  "About Us":             "about",
  "FAQ":                  "faq",
  "Shipping & Handling":  "shipping",
  "Returns":              "returns",
  "Privacy Policy":       "privacy",
  "Terms & Conditions":   "terms",
  "Terms of Sale":        "termsOfSale",
  "Accessibility":        "accessibility",
};

const SOCIAL_ICONS = {
  instagram_url: (
    <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.6" strokeLinecap="round" strokeLinejoin="round">
      <rect x="2" y="2" width="20" height="20" rx="5" ry="5"/><path d="M16 11.37A4 4 0 1 1 12.63 8 4 4 0 0 1 16 11.37z"/><line x1="17.5" y1="6.5" x2="17.51" y2="6.5"/>
    </svg>
  ),
  youtube_url: (
    <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.6" strokeLinecap="round" strokeLinejoin="round">
      <path d="M22.54 6.42a2.78 2.78 0 0 0-1.95-1.95C18.88 4 12 4 12 4s-6.88 0-8.59.47a2.78 2.78 0 0 0-1.95 1.95A29 29 0 0 0 1 12a29 29 0 0 0 .46 5.58A2.78 2.78 0 0 0 3.41 19.53C5.12 20 12 20 12 20s6.88 0 8.59-.47a2.78 2.78 0 0 0 1.95-1.95A29 29 0 0 0 23 12a29 29 0 0 0-.46-5.58z"/>
      <polygon points="9.75 15.02 15.5 12 9.75 8.98 9.75 15.02"/>
    </svg>
  ),
  facebook_url: (
    <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.6" strokeLinecap="round" strokeLinejoin="round">
      <path d="M18 2h-3a5 5 0 0 0-5 5v3H7v4h3v8h4v-8h3l1-4h-4V7a1 1 0 0 1 1-1h3z"/>
    </svg>
  ),
  tiktok_url: (
    <svg width="18" height="18" viewBox="0 0 24 24" fill="currentColor">
      <path d="M19.59 6.69a4.83 4.83 0 0 1-3.77-4.25V2h-3.45v13.67a2.89 2.89 0 0 1-2.88 2.5 2.89 2.89 0 0 1-2.89-2.89 2.89 2.89 0 0 1 2.89-2.89c.28 0 .54.04.79.1V9.01a6.36 6.36 0 0 0-.79-.05 6.34 6.34 0 0 0-6.34 6.34 6.34 6.34 0 0 0 6.34 6.34 6.34 6.34 0 0 0 6.33-6.34V9.06a8.23 8.23 0 0 0 4.84 1.55V7.16a4.85 4.85 0 0 1-1.07-.47z"/>
    </svg>
  ),
  linkedin_url: (
    <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.6" strokeLinecap="round" strokeLinejoin="round">
      <path d="M16 8a6 6 0 0 1 6 6v7h-4v-7a2 2 0 0 0-2-2 2 2 0 0 0-2 2v7h-4v-7a6 6 0 0 1 6-6z"/><rect x="2" y="9" width="4" height="12"/><circle cx="4" cy="4" r="2"/>
    </svg>
  ),
  twitter_url: (
    <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.6" strokeLinecap="round" strokeLinejoin="round">
      <path d="M23 3a10.9 10.9 0 0 1-3.14 1.53 4.48 4.48 0 0 0-7.86 3v1A10.66 10.66 0 0 1 3 4s-4 9 5 13a11.64 11.64 0 0 1-7 2c9 5 20 0 20-11.5a4.5 4.5 0 0 0-.08-.83A7.72 7.72 0 0 0 23 3z"/>
    </svg>
  ),
};

const DEFAULTS = {
  company_name: "Vendbabee",
  tagline: "Smart vending, effortlessly managed.",
  address_line1: "",
  address_line2: "",
  city: "",
  state: "",
  zip_code: "",
  country: "United States",
  phone: "",
  email: "",
  support_email: "",
  footer_columns: [
    {
      heading: "Company",
      links: [
        { label: "About Us",     href: "#" },
        { label: "Our Products", href: "/shop" },
        { label: "B2B Partners", href: "/" },
        { label: "Contact",      href: "mailto:info@vendbabee.com" },
      ],
    },
    {
      heading: "Support",
      links: [
        { label: "Order Status",        href: "/track" },
        { label: "Shipping & Handling", href: "#" },
        { label: "Returns",             href: "#" },
        { label: "FAQ",                 href: "#" },
      ],
    },
    {
      heading: "Legal",
      links: [
        { label: "Privacy Policy",     href: "#" },
        { label: "Terms & Conditions", href: "#" },
        { label: "Terms of Sale",      href: "#" },
        { label: "Accessibility",      href: "#" },
      ],
    },
  ],
  newsletter_disclaimer: `By entering your email and clicking "Sign Up", you agree to hear from Vendbabee.`,
  instagram_url: "",
  youtube_url: "",
  facebook_url: "",
  tiktok_url: "",
  linkedin_url: "",
  twitter_url: "",
};

export default function SiteFooter() {
  const [info, setInfo] = useState(DEFAULTS);
  const [email, setEmail] = useState("");
  const [subStatus, setSubStatus] = useState(null); // null | "ok" | "err"
  const [activeModal, setActiveModal] = useState(null);

  useEffect(() => {
    fetch(`${API}/site-info`)
      .then(r => r.json())
      .then(d => {
        if (d && d.key) {
          setInfo(prev => ({
            ...prev,
            ...Object.fromEntries(Object.entries(d).filter(([, v]) => v != null)),
          }));
        }
      })
      .catch(() => {/* use defaults */});
  }, []);

  const handleSubscribe = (e) => {
    e.preventDefault();
    if (!email.trim()) return;
    setSubStatus("ok");
    setEmail("");
    setTimeout(() => setSubStatus(null), 4000);
  };

  const handleLinkClick = (e, label) => {
    const modalKey = LEGAL_LINK_MAP[label];
    if (modalKey) {
      e.preventDefault();
      setActiveModal(modalKey);
    }
  };

  const columns = info.footer_columns?.length ? info.footer_columns : DEFAULTS.footer_columns;
  const socials  = Object.entries(SOCIAL_ICONS).filter(([key]) => info[key]);

  const addrParts = [
    info.address_line1,
    info.address_line2,
    [info.city, info.state, info.zip_code].filter(Boolean).join(" "),
    info.country,
  ].filter(Boolean);

  return (
    <>
      <footer
        style={{
          background: "#050507",
          color: "#f0f0f5",
          borderTop: "1px solid rgba(255,255,255,0.06)",
          fontFamily: "'Manrope', sans-serif",
        }}
      >
        {/* ── Main grid ── */}
        <div
          style={{
            maxWidth: 1280,
            margin: "0 auto",
            padding: "60px 2rem 40px",
            display: "grid",
            gridTemplateColumns: "repeat(auto-fit, minmax(160px, 1fr))",
            gap: "2.5rem 3rem",
            alignItems: "start",
          }}
        >
          {/* Dynamic columns */}
          {columns.map((col, ci) => (
            <div key={ci}>
              <p
                style={{
                  fontSize: "0.68rem",
                  letterSpacing: "0.12em",
                  textTransform: "uppercase",
                  fontWeight: 700,
                  color: "rgba(248,247,255,0.5)",
                  marginBottom: "1rem",
                }}
              >
                {col.heading}
              </p>
              <ul style={{ listStyle: "none", padding: 0, margin: 0, display: "flex", flexDirection: "column", gap: "0.55rem" }}>
                {(col.links || []).map((lnk, li) => {
                  const isModal = !!LEGAL_LINK_MAP[lnk.label];
                  // All links use brand blue — modal links slightly muted at rest, nav links at full blue
                  const restColor = "rgba(91,140,255,0.8)";
                  const hoverColor = "#5B8CFF";
                  return (
                    <li key={li}>
                      <a
                        href={lnk.href || "/"}
                        onClick={(e) => handleLinkClick(e, lnk.label)}
                        style={{
                          fontSize: "0.8rem",
                          color: restColor,
                          textDecoration: "none",
                          transition: "color 0.15s",
                          cursor: "pointer",
                        }}
                        onMouseEnter={e => (e.currentTarget.style.color = hoverColor)}
                        onMouseLeave={e => (e.currentTarget.style.color = restColor)}
                      >
                        {lnk.label}
                      </a>
                    </li>
                  );
                })}
              </ul>
            </div>
          ))}

          {/* Newsletter + socials */}
          <div style={{ minWidth: 220 }}>
            <p
              style={{
                fontSize: "0.68rem",
                letterSpacing: "0.12em",
                textTransform: "uppercase",
                fontWeight: 700,
                color: "rgba(248,247,255,0.5)",
                marginBottom: "1rem",
              }}
            >
              Join our newsletter
            </p>

            {info.newsletter_disclaimer && (
              <p style={{ fontSize: "0.73rem", color: "rgba(248,247,255,0.5)", lineHeight: 1.55, marginBottom: "1rem" }}>
                {info.newsletter_disclaimer}
              </p>
            )}

            <form onSubmit={handleSubscribe} style={{ display: "flex", flexDirection: "column", gap: "0.5rem" }}>
              <input
                type="email"
                placeholder="Enter your email address"
                value={email}
                onChange={e => setEmail(e.target.value)}
                required
                style={{
                  background: "rgba(248,247,255,0.05)",
                  border: "1px solid rgba(99,91,255,0.25)",
                  borderRadius: 6,
                  color: "#f8f7ff",
                  fontSize: "0.8rem",
                  padding: "0.55rem 0.8rem",
                  outline: "none",
                  width: "100%",
                }}
              />
              <button
                type="submit"
                style={{
                  background: "transparent",
                  border: "none",
                  color: "rgba(248,247,255,0.65)",
                  fontSize: "0.75rem",
                  fontWeight: 600,
                  letterSpacing: "0.05em",
                  padding: "0.25rem 0",
                  cursor: "pointer",
                  textAlign: "left",
                  textDecoration: "underline",
                  textUnderlineOffset: 3,
                }}
              >
                {subStatus === "ok" ? "✓ You're in!" : "Sign Up"}
              </button>
            </form>

            {/* Socials */}
            {socials.length > 0 && (
              <div style={{ display: "flex", gap: "0.8rem", marginTop: "1.5rem", flexWrap: "wrap" }}>
                {socials.map(([key, icon]) => (
                  <a
                    key={key}
                    href={info[key]}
                    target="_blank"
                    rel="noopener noreferrer"
                    style={{ color: "rgba(248,247,255,0.5)", transition: "color 0.15s" }}
                    onMouseEnter={e => (e.currentTarget.style.color = "#f8f7ff")}
                    onMouseLeave={e => (e.currentTarget.style.color = "rgba(248,247,255,0.5)")}
                  >
                    {icon}
                  </a>
                ))}
              </div>
            )}
          </div>
        </div>

        {/* ── Bottom bar ── */}
        <div
          style={{
            maxWidth: 1280,
            margin: "0 auto",
            padding: "1.25rem 2rem",
            borderTop: "1px solid rgba(248,247,255,0.07)",
            display: "flex",
            alignItems: "center",
            justifyContent: "space-between",
            flexWrap: "wrap",
            gap: "0.75rem",
          }}
        >
          <div style={{ display: "flex", alignItems: "center", gap: "0.5rem" }}>
            <span style={{ fontSize: "0.7rem", color: "rgba(248,247,255,0.35)" }}>📍</span>
            <span style={{ fontSize: "0.7rem", color: "rgba(248,247,255,0.4)" }}>
              {addrParts.length ? addrParts[addrParts.length - 1] : "United States"}
            </span>
          </div>

          <p
            style={{
              fontFamily: "'Oswald', sans-serif",
              fontSize: "0.85rem",
              fontWeight: 600,
              letterSpacing: "0.08em",
              color: "rgba(248,247,255,0.9)",
            }}
          >
            {info.company_name}
          </p>

          <p style={{ fontSize: "0.65rem", color: "rgba(248,247,255,0.25)" }}>
            © {new Date().getFullYear()} {info.company_name}. All rights reserved.
          </p>
        </div>
      </footer>

      {/* ── Modals ── */}
      <LegalModal
        modalKey={activeModal}
        open={!!activeModal}
        onOpenChange={(open) => { if (!open) setActiveModal(null); }}
      />
    </>
  );
}
